/**
 * IT > Service Desk (/it/service-desk). The working queue: four count tiles
 * and the ticket grid. Gated by `it.servicedesk`. Sub-pages: a ticket
 * (/tickets/:id), the knowledge base (/kb), and settings (/settings), all
 * reached from the tabs at the top.
 *
 * The grid follows the Cockpit standard: the shared `DataGridShell` with
 * `SortableHeader` column headers (click to sort), infinite scroll, and filters
 * kept in the address bar. The tiles and the filter dropdowns fold away so the
 * grid gets the height on a small screen, and each person's choice is
 * remembered in their browser. Sorting is explained in `queueGrid.ts`.
 */

import { useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { SortableHeader, gridTheadClassName, useIsMobile } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { Pill } from '@/components/Pill';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { RelativeTime } from '@/lib/RelativeTime';
import { usePersistedFlag } from '@/lib/usePersistedFlag';
import { useSession } from '@/lib/useSession';
import { useQueue, useStats } from '@/lib/useServiceDesk';
import type { SdQueueFilters, SdStaffTicketRow } from '@/lib/serviceDeskTypes';
import { QueueFilters } from './QueueFilters';
import { ReporterContact } from './ReporterContact';
import { SdShell } from './SdShell';
import {
  OPEN_STATUSES,
  PRIORITY_LABELS,
  PRIORITY_TONES,
  SLA_TONES,
  STATUS_LABELS,
  STATUS_TONES,
  slaChipText,
} from './sdLabels';
import { SD_CARD, SD_MUTED } from './sdStyles';
import {
  COLUMN_ORDER,
  DEFAULT_FILTERS,
  QUEUE_COLUMNS,
  nextSort,
  readQueueParams,
  sortRows,
  writeQueueParams,
  type QueueColumn,
  type QueueSort,
} from './queueGrid';

function Tile({
  label,
  value,
  hint,
  onClick,
  tone,
}: {
  label: string;
  value: number | undefined;
  hint: string;
  onClick?: () => void;
  tone?: 'bad';
}) {
  const body = (
    <>
      <span className={`block text-[12px] font-semibold uppercase tracking-caps-pill ${SD_MUTED}`}>{label}</span>
      <span
        className={`mt-1 block text-3xl font-semibold ${
          tone === 'bad' && (value ?? 0) > 0 ? 'text-red-700 dark:text-red-300' : 'text-amp-ink dark:text-amp-dark-ink'
        }`}
      >
        {value ?? '--'}
      </span>
      <span className={`mt-1 block text-[12px] ${SD_MUTED}`}>{hint}</span>
    </>
  );
  return onClick ? (
    <button type="button" onClick={onClick} className={`${SD_CARD} text-left hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm`}>
      {body}
    </button>
  ) : (
    <div className={SD_CARD}>{body}</div>
  );
}

function Row({ row }: { row: SdStaffTicketRow }) {
  const navigate = useNavigate();
  const go = () => navigate(`/it/service-desk/tickets/${row.id}`);
  // One line per ticket, so a screen shows as many tickets as it can. Long
  // titles and category paths are cut short and carry their full text as a tooltip.
  const cell = 'whitespace-nowrap px-2.5 py-1.5 align-middle';
  return (
    <tr
      onClick={go}
      className="cursor-pointer hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm"
    >
      <td className={`${cell} font-semibold`}>
        <Link
          to={`/it/service-desk/tickets/${row.id}`}
          onClick={(e) => e.stopPropagation()}
          className="text-amp-olive-deep underline-offset-2 hover:underline dark:text-amp-olive-dark"
        >
          {row.display_key}
        </Link>
        {row.unread_count > 0 && (
          <span className="ml-1.5 rounded-pill bg-amp-olive-deep px-1.5 text-[11px] text-amp-cream" title="New replies">
            {row.unread_count}
          </span>
        )}
      </td>
      <td className="max-w-[240px] truncate px-2.5 py-1.5 align-middle text-amp-ink dark:text-amp-dark-ink" title={row.title}>
        {row.title}
      </td>
      <td className={cell}>
        <ReporterContact name={row.reporter_name} email={row.reporter_email} />
      </td>
      <td className={cell}>{row.store_no ?? '--'}</td>
      <td className={`max-w-[160px] truncate px-2.5 py-1.5 align-middle text-[13px] ${SD_MUTED}`} title={row.category_path}>
        {row.category_path}
      </td>
      <td className={cell}>
        <Pill tone={PRIORITY_TONES[row.priority]}>{PRIORITY_LABELS[row.priority]}</Pill>
      </td>
      <td className={cell}>
        <Pill tone={STATUS_TONES[row.status]}>{STATUS_LABELS[row.status]}</Pill>
      </td>
      <td className={cell}>{row.assignee_name ?? <span className={SD_MUTED}>Unassigned</span>}</td>
      <td className={cell}>
        <Pill tone={SLA_TONES[row.sla_state]}>{slaChipText(row)}</Pill>
      </td>
      <td className={`${cell} ${SD_MUTED}`}><RelativeTime value={row.last_activity_at} /></td>
    </tr>
  );
}

/** The four counts as one line, shown when the tiles are folded away. */
function TileSummary({ stats }: { stats: ReturnType<typeof useStats>['data'] }) {
  const items: [string, number | undefined, boolean?][] = [
    ['Open', stats?.open],
    ['Unassigned', stats?.unassigned],
    ['Late', stats?.breached, true],
    ['Due today', stats?.due_today],
  ];
  return (
    <span className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px]">
      {items.map(([label, value, bad]) => (
        <span key={label} className={SD_MUTED}>
          {label}{' '}
          <strong
            className={
              bad && (value ?? 0) > 0 ? 'text-red-700 dark:text-red-300' : 'text-amp-ink dark:text-amp-dark-ink'
            }
          >
            {value ?? '--'}
          </strong>
        </span>
      ))}
    </span>
  );
}

export function ServiceDesk() {
  const { user } = useSession();
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const { filters, sort } = useMemo(() => readQueueParams(params), [params]);

  // Folded-away panels are remembered per person, in this browser.
  const who = (user?.email ?? 'anon').toLowerCase();
  const [tilesCollapsed, setTilesCollapsed] = usePersistedFlag(`sd-queue-tiles-collapsed:${who}`);
  const [filtersCollapsed, setFiltersCollapsed] = usePersistedFlag(`sd-queue-filters-collapsed:${who}`);

  const stats = useStats();
  const queue = useQueue(filters);

  const write = useCallback(
    (nextFilters: SdQueueFilters, nextSort: QueueSort | null) => {
      setParams(writeQueueParams(nextFilters, nextSort), { replace: true });
    },
    [setParams],
  );
  const setFilters = useCallback((next: SdQueueFilters) => write(next, sort), [write, sort]);
  const toggleSort = (column: QueueColumn) => write(filters, nextSort(sort, column));
  const sortedFor = (column: QueueColumn) => (sort?.column === column ? sort.dir : null);

  const loaded = useMemo(() => queue.data?.pages.flatMap((p) => p.rows) ?? [], [queue.data]);
  const rows = useMemo(() => sortRows(loaded, sort), [loaded, sort]);

  const activeCount =
    (filters.priority.length > 0 ? 1 : 0) +
    (filters.assignee ? 1 : 0) +
    (filters.category_id !== null ? 1 : 0) +
    (filters.store_no ? 1 : 0) +
    (filters.sla ? 1 : 0) +
    (filters.q ? 1 : 0) +
    (filters.status.length !== OPEN_STATUSES.length ? 1 : 0);

  return (
    <SdShell
      title="Service Desk"
      subtitle="Everything people have asked IT for. Open a ticket to reply, assign it, or change its status."
      tab="queue"
      actions={<ToolPinToggle toolSlug="service-desk" toolName="Service Desk" />}
      tabsAside={
        <span className="flex flex-wrap items-center gap-x-3">
          {tilesCollapsed && <TileSummary stats={stats.data} />}
          <button
            type="button"
            onClick={() => setTilesCollapsed(!tilesCollapsed)}
            aria-expanded={!tilesCollapsed}
            aria-controls="sd-queue-tiles"
            className={`inline-flex items-center gap-1 rounded-md text-[12px] font-semibold uppercase tracking-caps-pill ${SD_MUTED} hover:text-amp-ink dark:hover:text-amp-dark-ink`}
          >
            {tilesCollapsed ? <ChevronRight className="h-3.5 w-3.5" aria-hidden /> : <ChevronDown className="h-3.5 w-3.5" aria-hidden />}
            Summary
          </button>
        </span>
      }
    >
      {!tilesCollapsed && (
        <div id="sd-queue-tiles" className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Tile
            label="Open"
            value={stats.data?.open}
            hint="Not yet resolved"
            onClick={() => write({ ...DEFAULT_FILTERS }, sort)}
          />
          <Tile
            label="Unassigned"
            value={stats.data?.unassigned}
            hint="Nobody owns these yet"
            onClick={() => write({ ...DEFAULT_FILTERS, assignee: 'unassigned' }, sort)}
          />
          <Tile
            label="Late"
            value={stats.data?.breached}
            hint="Past their deadline"
            tone="bad"
            onClick={() => write({ ...DEFAULT_FILTERS, sla: 'breached' }, sort)}
          />
          <Tile label="Due today" value={stats.data?.due_today} hint="Deadline falls today" />
        </div>
      )}

      <DataGridShell
        className="min-h-[340px]"
        total={null}
        loaded={rows.length}
        countNoun="tickets"
        toolbar={
          <QueueFilters
            filters={filters}
            onChange={setFilters}
            activeCount={activeCount}
            collapsed={isMobile ? undefined : filtersCollapsed}
            onToggleCollapsed={isMobile ? undefined : () => setFiltersCollapsed(!filtersCollapsed)}
          />
        }
        activeFilterCount={activeCount}
        hasMore={!!queue.hasNextPage}
        isLoadingMore={queue.isFetchingNextPage}
        onReachEnd={() => void queue.fetchNextPage()}
        footer={
          queue.hasNextPage ? (
            <div className="flex justify-center p-2">
              <button
                type="button"
                onClick={() => void queue.fetchNextPage()}
                disabled={queue.isFetchingNextPage}
                className="rounded-md border border-amp-line px-3 py-1.5 text-[13px] font-semibold text-amp-ink hover:bg-amp-cream-warm disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
              >
                {queue.isFetchingNextPage ? 'Loading...' : 'Load more'}
              </button>
            </div>
          ) : undefined
        }
      >
        <table className="min-w-full text-[13px]">
          <thead className={gridTheadClassName}>
            <tr>
              {COLUMN_ORDER.map((c) => (
                <SortableHeader
                  key={c}
                  label={QUEUE_COLUMNS[c].label}
                  sorted={sortedFor(c)}
                  onToggle={() => toggleSort(c)}
                  thClassName="px-2.5 py-2 whitespace-nowrap"
                />
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {queue.isLoading ? (
              <tr>
                <td colSpan={10} className={`px-4 py-6 ${SD_MUTED}`}>
                  Loading tickets...
                </td>
              </tr>
            ) : queue.error ? (
              <tr>
                <td colSpan={10} className={`px-4 py-6 ${SD_MUTED}`}>
                  The queue is not available yet.
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={10} className={`px-4 py-6 ${SD_MUTED}`}>
                  No tickets match these filters.
                </td>
              </tr>
            ) : (
              rows.map((r) => <Row key={r.id} row={r} />)
            )}
          </tbody>
        </table>
      </DataGridShell>
    </SdShell>
  );
}
